import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, Copy, Plus } from "lucide-react";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { BaitPicker, type BaitSelection } from "../components/BaitPicker";
import { RigPicker, type RigSelection } from "../components/RigPicker";
import {
  LeadSystemPicker,
  type LeadSystemSelection,
  emptyLeadSystem,
} from "../components/LeadSystemPicker";

const STEPS = ["lead", "rig", "hookbait"] as const;
type RodStep = (typeof STEPS)[number];
type HookComponent = {
  bait: BaitSelection;
  size_mm: string;
  presentation: string;
  colour: string;
};

const emptyRig = (): RigSelection => ({
  canonical_id: null,
  user_rig_id: null,
  snapshot: "",
});
const emptyBait = (): BaitSelection => ({
  canonical_id: null,
  user_bait_id: null,
  snapshot: "",
});
const PRESENTATIONS = ["bottom bait", "wafter", "pop-up", "zig foam", "topper", "snowman component", "other"];

function progressKey(sessionId: string) {
  return `catchcompass:guided-setup:${sessionId}`;
}

function inferLeadSuggestion(advice: any) {
  const haystack = `${advice?.recommended_rig ?? ""} ${advice?.recommended_tactic ?? ""}`.toLowerCase();
  if (haystack.includes("helicopter")) return "Helicopter";
  if (haystack.includes("lead clip")) return "Lead clip";
  if (haystack.includes("drop-off inline")) return "Drop-off inline";
  if (haystack.includes("inline")) return "Inline";
  if (haystack.includes("running lead")) return "Running lead";
  return "";
}

function hookSummary(components: HookComponent[]) {
  return components
    .filter((component) => component.bait.snapshot.trim())
    .map((component) =>
      [
        component.bait.snapshot,
        component.size_mm ? `${component.size_mm}mm` : "",
        component.presentation,
        component.colour,
      ]
        .filter(Boolean)
        .join(" "),
    )
    .join(" + ");
}

export default function SessionSetupPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const sessionQuery = useQuery({
    queryKey: ["carpintel_session", id],
    queryFn: () =>
      fetch(`/app-api/sessions/${id}`).then((r) => {
        if (!r.ok) throw new Error("Session not found");
        return r.json();
      }),
  });
  const anglersQuery = useQuery({
    queryKey: ["carpintel_session_anglers", id],
    queryFn: () => fetch(`/app-api/sessions/${id}/anglers`).then((r) => (r.ok ? r.json() : [])),
    enabled: Boolean(id),
  });
  const stateQuery = useQuery({
    queryKey: ["bankside_rod_state", id],
    queryFn: () => fetch(`/app-api/sessions/${id}/rod-state`).then((r) => (r.ok ? r.json() : { states: [] })),
    enabled: Boolean(id),
  });

  const session = sessionQuery.data;
  const anglers: any[] = anglersQuery.data ?? [];
  const stateRows: any[] = stateQuery.data?.states ?? [];

  const adviceId = session?.advisor_recommendation_id;
  const adviceQuery = useQuery({
    queryKey: ["carpintel_advisor_recommendation", adviceId],
    queryFn: () => fetch(`/app-api/advisor/recommendations/${adviceId}`).then((r) => (r.ok ? r.json() : null)),
    enabled: Boolean(adviceId),
  });
  const advice = adviceQuery.data;

  const rodRefs = useMemo(
    () =>
      anglers.flatMap((angler) =>
        Array.from({ length: Math.max(1, Number(angler.rods_count || 1)) }, (_, index) => ({
          anglerId: Number(angler.id),
          rod: index + 1,
          label: anglers.length > 1 ? `${angler.display_name} · Rod ${index + 1}` : `Rod ${index + 1}`,
        })),
      ),
    [anglers],
  );

  const [rodIndex, setRodIndex] = useState(0);
  const [step, setStep] = useState<RodStep>("lead");
  const [durationStep, setDurationStep] = useState(false);
  const [useAdvice, setUseAdvice] = useState(true);
  const [initialised, setInitialised] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const [lead, setLead] = useState<LeadSystemSelection>(emptyLeadSystem());
  const [rig, setRig] = useState<RigSelection>(emptyRig());
  const [hookComponents, setHookComponents] = useState<HookComponent[]>([
    { bait: emptyBait(), size_mm: "", presentation: "", colour: "" },
  ]);
  const [durationChoice, setDurationChoice] = useState("12");
  const [customHours, setCustomHours] = useState("8");

  const currentRod = rodRefs[rodIndex];

  useEffect(() => {
    if (initialised || !rodRefs.length || stateQuery.isLoading) return;
    try {
      const saved = JSON.parse(localStorage.getItem(progressKey(id)) || "null");
      if (saved && Number.isInteger(saved.rodIndex) && STEPS.includes(saved.step)) {
        setRodIndex(Math.min(Math.max(saved.rodIndex, 0), rodRefs.length - 1));
        setStep(saved.step);
      }
    } catch {
      // A broken local progress marker must never block fishing.
    }
    setInitialised(true);
  }, [id, initialised, rodRefs, stateQuery.isLoading]);

  useEffect(() => {
    if (!currentRod || !initialised) return;
    const current = (component: string) =>
      stateRows.find(
        (row) =>
          Number(row.session_angler_id) === currentRod.anglerId &&
          Number(row.rod_number) === currentRod.rod &&
          String(row.component_type) === component,
      );
    const leadRow = current("lead_system");
    const rigRow = current("rig");
    const hookRow = current("hookbait");

    setLead({
      canonical_id: leadRow?.canonical_ref_id ?? null,
      user_lead_system_id: leadRow?.user_ref_id ?? null,
      snapshot: leadRow?.snapshot ?? (useAdvice ? inferLeadSuggestion(advice) : ""),
    });
    setRig({
      canonical_id: rigRow?.canonical_ref_id ?? null,
      user_rig_id: rigRow?.user_ref_id ?? null,
      snapshot: rigRow?.snapshot ?? (useAdvice ? String(advice?.recommended_rig ?? "") : ""),
    });

    const existingComponents = Array.isArray(hookRow?.details_json?.components)
      ? hookRow.details_json.components
      : [];
    if (existingComponents.length) {
      setHookComponents(
        existingComponents.map((item: any) => ({
          bait: {
            canonical_id: item?.bait?.canonical_id ?? null,
            user_bait_id: item?.bait?.user_bait_id ?? null,
            snapshot: item?.bait?.snapshot ?? "",
          },
          size_mm: item?.size_mm == null ? "" : String(item.size_mm),
          presentation: String(item?.presentation ?? ""),
          colour: String(item?.colour ?? ""),
        })),
      );
    } else {
      setHookComponents([
        {
          bait: {
            ...emptyBait(),
            snapshot: useAdvice ? String(advice?.recommended_hookbait ?? "") : "",
          },
          size_mm: "",
          presentation: "",
          colour: "",
        },
      ]);
    }
  }, [currentRod?.anglerId, currentRod?.rod, initialised, useAdvice, advice?.id]);

  function rememberProgress(nextRodIndex: number, nextStep: RodStep) {
    localStorage.setItem(progressKey(id), JSON.stringify({ rodIndex: nextRodIndex, step: nextStep }));
  }

  async function refreshState() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["bankside_rod_state", id] }),
      queryClient.invalidateQueries({ queryKey: ["carpintel_session_rod_setups", id] }),
    ]);
  }

  function advanceAfterRod() {
    if (rodIndex < rodRefs.length - 1) {
      const nextIndex = rodIndex + 1;
      setRodIndex(nextIndex);
      setStep("lead");
      rememberProgress(nextIndex, "lead");
      return;
    }
    setDurationStep(true);
  }

  function advance() {
    const currentStep = STEPS.indexOf(step);
    if (currentStep < STEPS.length - 1) {
      const next = STEPS[currentStep + 1];
      setStep(next);
      rememberProgress(rodIndex, next);
      return;
    }
    advanceAfterRod();
  }

  async function saveComponent(component: "lead_system" | "rig" | "hookbait") {
    if (!currentRod) return;
    let body: any = { session_angler_id: currentRod.anglerId, rod_number: currentRod.rod };
    if (component === "lead_system") {
      if (!lead.snapshot.trim()) return setMessage("Choose a lead setup before continuing.");
      body = { ...body, canonical_ref_id: lead.canonical_id, user_ref_id: lead.user_lead_system_id, snapshot: lead.snapshot };
    }
    if (component === "rig") {
      if (!rig.snapshot.trim()) return setMessage("Choose a rig before continuing.");
      body = { ...body, canonical_ref_id: rig.canonical_id, user_ref_id: rig.user_rig_id, snapshot: rig.snapshot };
    }
    if (component === "hookbait") {
      const components = hookComponents.filter((item) => item.bait.snapshot.trim());
      if (!components.length) return setMessage("Choose a hookbait before continuing.");
      body = {
        ...body,
        canonical_ref_id: components[0]?.bait.canonical_id ?? null,
        user_ref_id: components[0]?.bait.user_bait_id ?? null,
        snapshot: hookSummary(components),
        details_json: { components },
      };
    }

    setBusy(true);
    setMessage("");
    try {
      const response = await fetch(`/app-api/sessions/${id}/rod-state/${component}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not save rod setup");
      await refreshState();
      advance();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not save rod setup");
    } finally {
      setBusy(false);
    }
  }

  async function copyRodOne() {
    if (!currentRod || rodIndex === 0 || !rodRefs[0]) return;
    setBusy(true);
    setMessage("");
    try {
      const response = await fetch(`/app-api/sessions/${id}/copy-rod-state`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source_session_angler_id: rodRefs[0].anglerId,
          source_rod_number: rodRefs[0].rod,
          targets: [{ session_angler_id: currentRod.anglerId, rod_number: currentRod.rod }],
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not copy Rod 1");
      await refreshState();
      setMessage("Rod 1 tackle copied. Position and feed will be chosen when this rod is actually cast.");
      advanceAfterRod();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not copy Rod 1");
    } finally {
      setBusy(false);
    }
  }

  async function finishSetup() {
    const hours = durationChoice === "custom" ? Number(customHours) : Number(durationChoice);
    if (!Number.isFinite(hours) || hours <= 0 || hours > 168) return setMessage("Choose a valid expected session length.");
    const start = new Date(session.start_at).getTime();
    const planned = new Date(start + hours * 60 * 60 * 1000);
    if (planned.getTime() <= Date.now()) return setMessage("That expected finish is already in the past. Choose a longer session length.");

    setBusy(true);
    setMessage("");
    try {
      const response = await fetch(`/app-api/sessions/${id}/planned-end`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ planned_end_at: planned.toISOString() }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not save planned finish");
      await fetch("/app-api/preferences", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ default_session_duration_hours: Math.round(hours) }),
      }).catch(() => undefined);
      localStorage.removeItem(progressKey(id));
      await queryClient.invalidateQueries({ queryKey: ["carpintel_session", id] });
      navigate(`/live/${id}`, { replace: true });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not finish session setup");
    } finally {
      setBusy(false);
    }
  }

  if (sessionQuery.isLoading || anglersQuery.isLoading || stateQuery.isLoading) {
    return <PageMessage text="Preparing your rods…" />;
  }
  if (sessionQuery.error || !session) return <PageMessage text="That session could not be found." />;
  if (session.end_at) return <Navigate to={`/sessions/${id}`} replace />;
  if (!rodRefs.length) return <PageMessage text="No rods are attached to this session." />;

  if (durationStep) {
    const hours = durationChoice === "custom" ? Number(customHours || 0) : Number(durationChoice);
    const finish = Number.isFinite(hours) && hours > 0
      ? new Date(new Date(session.start_at).getTime() + hours * 60 * 60 * 1000)
      : null;
    return (
      <div className="mx-auto w-full max-w-2xl space-y-5 px-4 py-6 pb-28">
        <header>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-accent">Start Fishing · Final setup step</p>
          <h1 className="mt-1 text-2xl font-bold text-primary">How long are you expecting to fish?</h1>
          <p className="mt-2 text-sm leading-6 text-secondary">After this, CatchCompass will take you straight into Deploy your rods. Position and feed are recorded only when each rod is actually cast.</p>
        </header>
        <section className="rounded-2xl border border-border bg-raised p-4">
          <div className="grid grid-cols-4 gap-2">
            {["6", "12", "24", "48"].map((value) => (
              <button key={value} type="button" onClick={() => setDurationChoice(value)} className={`min-h-12 rounded-lg border text-sm font-bold ${durationChoice === value ? "border-accent bg-accent text-white" : "border-border bg-background text-primary"}`}>{value}h</button>
            ))}
          </div>
          <button type="button" onClick={() => setDurationChoice("custom")} className={`mt-2 min-h-11 w-full rounded-lg border text-sm font-semibold ${durationChoice === "custom" ? "border-accent bg-accent/10 text-accent" : "border-border text-primary"}`}>Custom</button>
          {durationChoice === "custom" ? <Input className="mt-3" type="number" min="1" max="168" step="1" value={customHours} onChange={(e) => setCustomHours(e.target.value)} placeholder="Hours" /> : null}
          {finish ? <p className="mt-4 rounded-lg bg-inset p-3 text-sm text-secondary">Expected finish: <strong className="text-primary">{finish.toLocaleString()}</strong></p> : null}
          <Button className="mt-4 w-full" size="lg" onClick={finishSetup} isLoading={busy}>Continue to Deploy your rods <ChevronRight /></Button>
        </section>
        {message ? <Status text={message} /> : null}
      </div>
    );
  }

  const stepNumber = STEPS.indexOf(step) + 1;
  const suggestion = step === "lead"
    ? inferLeadSuggestion(advice)
    : step === "rig"
      ? advice?.recommended_rig
      : advice?.recommended_hookbait;

  return (
    <div className="mx-auto w-full max-w-2xl space-y-5 px-4 py-6 pb-28">
      <header>
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-accent">Start Fishing · Rod setup</p>
        <h1 className="mt-1 text-2xl font-bold text-primary">{currentRod.label}</h1>
        <p className="mt-2 text-sm text-secondary">Step {stepNumber} of 3 · {step === "lead" ? "Lead setup" : step === "rig" ? "Rig" : "Hookbait"}</p>
        <div className="mt-3 flex gap-1.5">{STEPS.map((value, index) => <div key={value} className={`h-1.5 flex-1 rounded-full ${index <= STEPS.indexOf(step) ? "bg-accent" : "bg-inset"}`} />)}</div>
      </header>

      {advice ? (
        <section className="rounded-xl border border-accent/30 bg-accent/5 p-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-primary">CatchCompass advice</p>
              <p className="mt-1 text-xs leading-5 text-secondary">Use the recommendation as a starting point, or set this rod up your own way.</p>
            </div>
            <div className="flex shrink-0 gap-1">
              <button type="button" onClick={() => setUseAdvice(true)} className={`rounded-md px-2 py-1 text-xs font-semibold ${useAdvice ? "bg-accent text-white" : "bg-inset text-secondary"}`}>Use</button>
              <button type="button" onClick={() => setUseAdvice(false)} className={`rounded-md px-2 py-1 text-xs font-semibold ${!useAdvice ? "bg-accent text-white" : "bg-inset text-secondary"}`}>My setup</button>
            </div>
          </div>
          {useAdvice && suggestion ? <p className="mt-2 text-sm font-medium text-accent">Suggested: {String(suggestion)}</p> : null}
        </section>
      ) : null}

      {rodIndex > 0 && step === "lead" ? (
        <Button variant="secondary" className="w-full" onClick={copyRodOne} isLoading={busy}><Copy className="size-4" /> Copy Rod 1 tackle setup</Button>
      ) : null}

      <section className="rounded-2xl border border-border bg-raised p-4">
        {step === "lead" ? (
          <><LeadSystemPicker id="setup-lead" value={lead} onChange={setLead} speciesFilter={session.target_species} /><Button className="mt-4 w-full" onClick={() => saveComponent("lead_system")} isLoading={busy}>Continue to rig <ChevronRight /></Button></>
        ) : null}

        {step === "rig" ? (
          <><RigPicker id="setup-rig" label="Rig" value={rig} onChange={setRig} speciesFilter={session.target_species} /><Button className="mt-4 w-full" onClick={() => saveComponent("rig")} isLoading={busy}>Continue to hookbait <ChevronRight /></Button></>
        ) : null}

        {step === "hookbait" ? (
          <div className="space-y-4">
            {hookComponents.map((component, index) => (
              <div key={index} className="space-y-2 rounded-xl border border-border bg-background p-3">
                <BaitPicker id={`setup-hook-${index}`} label={index === 0 ? "Hookbait" : `Hookbait component ${index + 1}`} value={component.bait} onChange={(bait) => setHookComponents((current) => current.map((row, i) => i === index ? { ...row, bait } : row))} speciesFilter={session.target_species} presentationRole="hookbait" />
                <div className="grid grid-cols-3 gap-2">
                  <Input type="number" min="1" max="50" placeholder="mm" value={component.size_mm} onChange={(e) => setHookComponents((current) => current.map((row, i) => i === index ? { ...row, size_mm: e.target.value } : row))} />
                  <select className="h-11 rounded-lg border border-border bg-background px-2 text-xs text-primary" value={component.presentation} onChange={(e) => setHookComponents((current) => current.map((row, i) => i === index ? { ...row, presentation: e.target.value } : row))}><option value="">Presentation</option>{PRESENTATIONS.map((value) => <option key={value} value={value}>{value}</option>)}</select>
                  <Input placeholder="Colour" value={component.colour} onChange={(e) => setHookComponents((current) => current.map((row, i) => i === index ? { ...row, colour: e.target.value } : row))} />
                </div>
                {hookComponents.length > 1 ? <button type="button" className="text-xs font-semibold text-error" onClick={() => setHookComponents((current) => current.filter((_, i) => i !== index))}>Remove component</button> : null}
              </div>
            ))}
            {hookComponents.length < 3 ? <Button type="button" size="sm" variant="tertiary" onClick={() => setHookComponents((current) => [...current, { bait: emptyBait(), size_mm: "", presentation: "", colour: "" }])}><Plus className="size-4" /> Add topper / snowman component</Button> : null}
            <Button className="w-full" onClick={() => saveComponent("hookbait")} isLoading={busy}>{rodIndex < rodRefs.length - 1 ? `Complete ${currentRod.label} and set up next rod` : "Complete final rod setup"} <ChevronRight /></Button>
          </div>
        ) : null}
      </section>

      <section className="rounded-xl border border-border bg-background p-3 text-xs leading-5 text-secondary">
        Position and feed are deliberately not part of setup anymore. They are recorded from the Cast tap so CatchCompass only starts learning exposure when the rod is genuinely fishing.
      </section>

      {message ? <Status text={message} /> : null}
    </div>
  );
}

function Status({ text }: { text: string }) {
  return <p className="rounded-lg border border-border bg-raised p-3 text-sm text-secondary" role="status">{text}</p>;
}

function PageMessage({ text }: { text: string }) {
  return <div className="mx-auto max-w-2xl px-4 py-10 text-sm text-secondary">{text}</div>;
}
